export function dateDiffInDays(dateTime1: string, dateTime2: string): number {
  const date1 = new Date(dateTime1);
  const date2 = new Date(dateTime2);
  const diffInMilliseconds = Math.abs(date2.getTime() - date1.getTime());
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return diffInDays;
}

export function dateDiffInMonths(dateTime1: string, dateTime2: string): number {
  const date1 = new Date(dateTime1);
  const date2 = new Date(dateTime2);
  const yearsDiff = date2.getFullYear() - date1.getFullYear();
  const monthsDiff = date2.getMonth() - date1.getMonth();
  return yearsDiff * 12 + monthsDiff;
}

export const formatDate = (
  date: string | Date | number,
  format: string
): string => {
  if (!date || date === null) return 'Null';
  const options: Intl.DateTimeFormatOptions = {};
  let locale = 'en-US'; // Default locale is 'en-US'

  switch (format) {
    case 'MMM YYYY':
      options.year = 'numeric';
      options.month = 'short';
      break;
    case 'D MMM':
      options.day = 'numeric';
      options.month = 'short';
      break;
    case 'h:mm A':
      options.hour = 'numeric';
      options.minute = 'numeric';
      options.hour12 = true;
      break;
    case 'DD-MM-YYYY':
    case 'DD-MM-YYYY HH:mm:ss':
    case 'DD-MM-YYYY hh:mm:ss':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = 'numeric';
      locale = 'en-GB'; // Use 'en-GB' for DD-MM-YYYY format
      if (format.includes('HH:mm:ss') || format.includes('hh:mm:ss')) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.hour12 = format.includes('hh:mm:ss'); // Use 12-hour format if 'hh:mm:ss'
      }
      break;
    case 'MM-DD-YYYY':
      options.month = '2-digit';
      options.day = '2-digit';
      options.year = 'numeric';
      break;
    case 'HH:mm':
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = false;
      break;
    default:
      throw new Error(`Unsupported format: ${format}`);
  }

  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
};

export function formatDateInLocalStr(dateStr: string) {
  if (!dateStr) {
    return;
  }
  const date = new Date(dateStr);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return date.toLocaleDateString('en-US', options);
}

export function getDateRangesFor7Days(): {
  todayStart: string;
  sevenDaysAgoStart: string;
} {
  const now = new Date();

  // Helper function to format date in "YYYY-MM-DD HH:mm:ss"
  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // Today at 00:00:00
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0
  );

  // 7 days before today at 00:00:00
  const sevenDaysAgoStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 7,
    0,
    0,
    0
  );

  return {
    todayStart: formatDate(todayStart),
    sevenDaysAgoStart: formatDate(sevenDaysAgoStart)
  };
}

export const getFormattedCurrentDateTime = () => {
  const addLeadingZero = (num: number) => (num < 10 ? `0${num}` : num);

  const now = new Date();
  const year = now.getFullYear();
  const month = addLeadingZero(now.getMonth() + 1); // Months are 0-based
  const day = addLeadingZero(now.getDate());
  const hours = addLeadingZero(now.getHours()); // 24-hour format by default
  const minutes = addLeadingZero(now.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}
